/**
 * Main-thread entry point for the Audio Sidecar (PLAT-11696).
 *
 * Spins a dedicated worker per call, hands it the `File` (structured clone by
 * reference, no copy) and resolves with the worker's outcome. Every failure —
 * no Worker support, a crash, a timeout, an abort — resolves to a skip rather
 * than rejecting: the Sidecar is an accelerator and the caller always has the
 * Server-Extract Path.
 */
import { LogManager } from '@src/utility/Logger';

import { isSidecarDemuxResult } from './sidecarDemuxMessages';
import { skip } from './types';

import type { SidecarWorkerLike } from './sidecarDemuxMessages';
import type { DemuxOptions, SidecarOutcome } from './types';

const logger = LogManager.getLogger('upload:sidecar');

/** Generous: the orchestration gives up on its own once the video passes 50 %. */
export const DEFAULT_SIDECAR_TIMEOUT_MS = 120_000;

export interface CreateAudioSidecarOptions extends Pick<
  DemuxOptions,
  'chunkBytes' | 'maxSidecarRatio' | 'allowCo64Over4GiB'
> {
  timeoutMs?: number;
  signal?: AbortSignal;
  /** Injected by tests; defaults to a Vite module worker. */
  workerFactory?: () => SidecarWorkerLike | null;
}

export function defaultSidecarWorkerFactory(): SidecarWorkerLike | null {
  if (typeof Worker === 'undefined') return null;
  if (typeof navigator !== 'undefined' && /jsdom/i.test(navigator.userAgent)) {
    return null;
  }
  try {
    return new Worker(new URL('./sidecarDemux.worker.ts', import.meta.url), {
      type: 'module',
    });
  } catch (error) {
    logger.warn('sidecar-worker-init-failed', { message: String(error) });
    return null;
  }
}

let nextRequestId = 0;

export function createAudioSidecar(
  file: File,
  options: CreateAudioSidecarOptions = {},
): Promise<SidecarOutcome> {
  const factory = options.workerFactory ?? defaultSidecarWorkerFactory;
  const worker = factory();
  if (!worker) return Promise.resolve(skip('worker-unavailable'));

  const id = `sidecar_${Date.now()}_${(nextRequestId += 1)}`;
  const timeoutMs = options.timeoutMs ?? DEFAULT_SIDECAR_TIMEOUT_MS;

  return new Promise<SidecarOutcome>(resolve => {
    let settled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const settle = (outcome: SidecarOutcome) => {
      if (settled) return;
      settled = true;
      if (timer) clearTimeout(timer);
      options.signal?.removeEventListener('abort', onAbort);
      try {
        worker.terminate();
      } catch {
        /* ignore */
      }
      resolve(outcome);
    };

    const onAbort = () => {
      try {
        worker.postMessage({ type: 'cancel', id });
      } catch {
        /* ignore */
      }
      settle(skip('aborted'));
    };

    worker.addEventListener('message', ({ data }) => {
      if (!isSidecarDemuxResult(data) || data.id !== id) return;
      settle(data.outcome);
    });
    worker.addEventListener('error', event => {
      logger.warn('sidecar-worker-crashed', { message: event.message });
      settle(skip('worker-crashed', { detail: event.message }));
    });

    if (options.signal?.aborted) {
      onAbort();
      return;
    }
    options.signal?.addEventListener('abort', onAbort);
    timer = setTimeout(() => settle(skip('timeout')), timeoutMs);

    try {
      worker.postMessage({
        type: 'demux',
        id,
        file,
        options: {
          chunkBytes: options.chunkBytes,
          maxSidecarRatio: options.maxSidecarRatio,
          allowCo64Over4GiB: options.allowCo64Over4GiB,
        },
      });
    } catch (error) {
      settle(skip('worker-unavailable', { detail: String(error) }));
    }
  });
}
